import React from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";
import {
  getAllClientBoxes,
  getAllClientBrands,
  getAllClientProducerFarms,
  getAllClientProducers,
  getClientIdByToken,
  validTaskCodeByToken,
} from "../../../../libs/apis";
import {
  Button,
  Input,
  InputNumber,
  Select,
  Space,
  Divider,
  message,
  ConfigProvider,
  Collapse,
  Popconfirm,
} from "antd";
import { useWorkflowContainerFullStore } from "../../store";
import {
  ClientI,
  ClientProducerI,
  ClientProducerFarmI,
  ClientBoxI,
  ClientBrandI,
} from "../../../../libs/interfaces";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DateFormatHelper } from "../../../../libs/helpers";
import { Environments } from "../../../../libs/enums";
import { useRouterStore } from "../../../../libs/stores";
import { DividerComponent } from "../../../../libs/shared";
import { FileSyncOutlined } from "@ant-design/icons";

dayjs.extend(customParseFormat);
const { TextArea } = Input;

interface FirstComponentProps extends PropsGenericI {
  clients: ClientI[];
  enqueueSnackbar: (message: string, options?: any) => void;
}

interface PropsGenericI {
  width: string;
}

export const FirstComponent = ({
  clients,
  width,
  enqueueSnackbar,
}: FirstComponentProps) => {
  const [showExporters, setShowExporters] = React.useState(false);

  React.useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    const { data: dataTask } = await validTaskCodeByToken(
      Environments.TASK_CONTAINER_FULL_ASSIGN
    );
    if (!dataTask) return;
    if (dataTask.status) setShowExporters(true);
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="flex-start"
      alignContent="flex-start"
      alignItems="flex-start"
      sx={{
        minHeight: `calc(100vh - 105px)`,
        maxHeight: `calc(100vh - 105px)`,
        height: `calc(100vh - 105px)`,
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        pt: 7,
        width: "100%",
      }}
    >
      <NumeroContenedor />

      {showExporters ? null : (
        <>{width === "xs" ? null : <Grid item xs={3}></Grid>}</>
      )}
      <Exporters
        clients={clients}
        showExporters={showExporters}
        enqueueSnackbar={enqueueSnackbar}
      />

      <DividerComponent label="TRANSPORTE" />
      <Conductor width={width} />
      <CompañíaDeTransporte />

      <DividerComponent label="CALIDAD - GANCHO" />
      <Calidad width={width} />
      <Gancho width={width} />

      <DividerComponent label="HORAS DE PROCESO" />
      <CierreProceso />
      <FinDeCarga />
      <CierreContenedor />
      <SalidaContenedor />
      <DividerComponent label="OBSERVACIÓN" />
      <Observacion />
      <Grid item xs={12} sx={{ height: 20 }}></Grid>
    </Grid>
  );
};

const Cajas = () => {
  const numCajas = useWorkflowContainerFullStore((state) => state.numCajas);
  const setNumCajas = useWorkflowContainerFullStore(
    (state) => state.setNumCajas
  );

  return (
    <Grid item xs={6}>
      <InputNumber
        style={{ width: "100%" }}
        value={numCajas || null}
        onChange={(value) => setNumCajas(value)}
        addonBefore={
          <p
            style={{
              fontSize: "12px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            N° Cajas
          </p>
        }
      />
    </Grid>
  );
};

const CajasEstiba = () => {
  const cajasEstiba = useWorkflowContainerFullStore(
    (state) => state.cajasEstiba
  );
  const setCajasEstiba = useWorkflowContainerFullStore(
    (state) => state.setCajasEstiba
  );

  return (
    <Grid item xs={6}>
      <InputNumber
        style={{ width: "100%" }}
        value={cajasEstiba || null}
        onChange={(value) => setCajasEstiba(value)}
        addonBefore={
          <p
            style={{
              fontSize: "10px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
              textWrap: "wrap",
            }}
          >
            Cajas Doble Estiba
          </p>
        }
      />
    </Grid>
  );
};

const NumeroContenedor = () => {
  const container = useWorkflowContainerFullStore((state) => state.container);

  const setContainer = useWorkflowContainerFullStore(
    (state) => state.setContainer
  );

  return (
    <Grid item xs={12} sm={6} md={6}>
      <Input
        addonBefore={<p style={{ fontWeight: "bold" }}>N° Contenedor</p>}
        value={container}
        onChange={(e) => {
          setContainer(e.target.value.toUpperCase());
        }}
        onBlur={(e) => {
          setContainer(e.target.value.trim());
        }}
      />
    </Grid>
  );
};

const Calidad: React.FC<PropsGenericI> = ({ width }) => {
  const calidad = useWorkflowContainerFullStore((state) => state.calidad);
  const setCalidad = useWorkflowContainerFullStore((state) => state.setCalidad);

  const calidadCi = useWorkflowContainerFullStore((state) => state.calidadCi);
  const setCalidadCi = useWorkflowContainerFullStore(
    (state) => state.setCalidadCi
  );

  return (
    <>
      {width === "xs" ? (
        <>
          <Grid item xs={12}>
            <Input
              value={calidad || ""}
              onChange={(e) => setCalidad(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Calidad
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={calidadCi || ""}
              onChange={(e) => setCalidadCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Grid>
          <Divider dashed />
        </>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={calidad || ""}
              onChange={(e) => setCalidad(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Calidad
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
            <Input
              value={calidadCi || ""}
              onChange={(e) => setCalidadCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Space.Compact>
        </Grid>
      )}
    </>
  );
};

const Gancho: React.FC<PropsGenericI> = ({ width }) => {
  const gancho = useWorkflowContainerFullStore((state) => state.gancho);
  const setGancho = useWorkflowContainerFullStore((state) => state.setGancho);

  const ganchoCi = useWorkflowContainerFullStore((state) => state.ganchoCi);
  const setGanchoCi = useWorkflowContainerFullStore(
    (state) => state.setGanchoCi
  );

  return (
    <>
      {width === "xs" ? (
        <>
          <Grid item xs={12} sx={{ mt: -2 }}>
            <Input
              value={gancho || ""}
              onChange={(e) => setGancho(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Gancho
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={ganchoCi || ""}
              onChange={(e) => setGanchoCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={gancho || ""}
              onChange={(e) => setGancho(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Gancho
                </p>
              }
              placeholder="Nombre Y Apellido"
            />

            <Input
              value={ganchoCi || ""}
              onChange={(e) => setGanchoCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Space.Compact>
        </Grid>
      )}
    </>
  );
};

const CierreProceso = () => {
  const setCloseProcess = useWorkflowContainerFullStore(
    (state) => state.setCloseProcess
  );

  const closeProcess = useWorkflowContainerFullStore(
    (state) => state.closeProcess
  );

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setCloseProcess(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (closeProcess === "") {
      setCloseProcess(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(closeProcess)) {
      setCloseProcess(closeProcess); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setCloseProcess(closeProcess || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setCloseProcess(dateFormat.getTime());
          }}
          style={{ width: "150px", fontWeight: "bold" }}
        >
          Cierre de Proceso
        </Button>
        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          value={closeProcess ? dayjs(closeProcess, hourFormat) : null}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          onChange={(time, times) => {
            setCloseProcess(times.toString());
          }}
        /> */}
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={closeProcess || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />
      </Space.Compact>
    </Grid>
  );
};

const CierreContenedor = () => {
  const containerClose = useWorkflowContainerFullStore(
    (state) => state.containerClose
  );

  const setContainerClose = useWorkflowContainerFullStore(
    (state) => state.setContainerClose
  );

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Cambiar el formato de la hora
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setContainerClose(value); // Actualizar el valor del input
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (containerClose === "") {
      setContainerClose(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(containerClose)) {
      setContainerClose(containerClose); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setContainerClose(containerClose || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setContainerClose(dateFormat.getTime());
          }}
          style={{ width: "150px", fontWeight: "bold" }}
        >
          Cierre Contenedor
        </Button>
        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={containerClose ? dayjs(containerClose, hourFormat) : null}
          onChange={(tim, time) => {
            setContainerClose(time.toString());
          }}
        /> */}
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={containerClose || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />
      </Space.Compact>
    </Grid>
  );
};

const FinDeCarga = () => {
  const endLoading = useWorkflowContainerFullStore((state) => state.endLoading);

  const setEndLoading = useWorkflowContainerFullStore(
    (state) => state.setEndLoading
  );

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setEndLoading(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (endLoading === "") {
      setEndLoading(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(endLoading)) {
      setEndLoading(endLoading); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setEndLoading(endLoading || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setEndLoading(dateFormat.getTime());
          }}
          style={{ width: "150px", fontWeight: "bold" }}
        >
          Fin de Carga
        </Button>
        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={endLoading ? dayjs(endLoading, hourFormat) : null}
          onChange={(tim, time) => {
            setEndLoading(time.toString());
          }}
        /> */}
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={endLoading || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />
      </Space.Compact>
    </Grid>
  );
};

const SalidaContenedor = () => {
  const containerOulet = useWorkflowContainerFullStore(
    (state) => state.containerOulet
  );

  const setContainerOulet = useWorkflowContainerFullStore(
    (state) => state.setContainerOulet
  );

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setContainerOulet(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (containerOulet === "") {
      setContainerOulet(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(containerOulet)) {
      setContainerOulet(containerOulet); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setContainerOulet(containerOulet || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setContainerOulet(dateFormat.getTime());
          }}
          style={{ width: "150px", fontWeight: "bold" }}
        >
          Salida Contenedor
        </Button>
        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={containerOulet ? dayjs(containerOulet, hourFormat) : null}
          onChange={(tim, time) => {
            setContainerOulet(time.toString());
          }}
        /> */}
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={containerOulet || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />
      </Space.Compact>
    </Grid>
  );
};

const Observacion = () => {
  const observation = useWorkflowContainerFullStore(
    (state) => state.observation
  );

  const setObservation = useWorkflowContainerFullStore(
    (state) => state.setObservation
  );

  return (
    <Grid item xs={12}>
      <TextArea
        rows={4}
        placeholder="Observación"
        maxLength={500}
        showCount
        value={observation || ""}
        onChange={(e) => setObservation(e.target.value)}
      />
    </Grid>
  );
};

interface ClientsProps {
  clients: ClientI[];
  showExporters: boolean;
  enqueueSnackbar: (message: string, options?: any) => void;
}

const Exporters = ({
  clients,
  showExporters,
  enqueueSnackbar,
}: ClientsProps) => {
  const elClientId = useRouterStore((state) => state.clientId);

  const wfClientId = useWorkflowContainerFullStore((state) => state.clientId);
  const producerId = useWorkflowContainerFullStore((state) => state.producerId);

  const setWorkflowClientId = useWorkflowContainerFullStore(
    (state) => state.setClientId
  );
  const setWorkflowClient = useWorkflowContainerFullStore(
    (state) => state.setClient
  );
  const resetWorkflowClient = useWorkflowContainerFullStore(
    (state) => state.resetClient
  );

  const clientName = useWorkflowContainerFullStore((state) => state.client);

  const [producers, setProducers] = React.useState<ClientProducerI[]>([]);
  const [farms, setFarms] = React.useState<ClientProducerFarmI[]>([]);
  const [boxes, setBoxes] = React.useState<ClientBoxI[]>([]);
  const [brands, setBrands] = React.useState<ClientBrandI[]>([]);
  const resetProducer = useWorkflowContainerFullStore(
    (state) => state.resetProducer
  );
  const setProducer = useWorkflowContainerFullStore((state) => state.setProducer);
  const setProducerId = useWorkflowContainerFullStore(
    (state) => state.setProducerId
  );

  React.useEffect(() => {
    (async () => {
      try {
        // Si es un usuario normal (NO admin) y tiene elClientId
        if (elClientId) {
          // Guardamos ese clientId en el workflow store, para que
          // todo el flujo use el MISMO ID de cliente.
          setWorkflowClientId(elClientId);
          await loadClientData(elClientId);
        } else {
          // Si es un usuario admin (no tiene elClientId)...
          // 1) Revisa si ya tenemos un clientId en workflow store
          if (wfClientId) {
            // Si lo tenemos, cargamos su data
            await loadClientData(wfClientId);
          } else {
            // 2) Si no tenemos, tratamos de obtenerlo por token
            const { data: dataClient } = await getClientIdByToken();
            if (dataClient) {
              setWorkflowClientId(dataClient);
              await loadClientData(dataClient);
            }
          }
        }

        if (producerId) {
          await loadFarms(producerId);
        }
      } catch (err: any) {
        enqueueSnackbar(err.toString(), { variant: "error" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------
  // 2) Función para cargar datos principales de un "cliente"
  // ----------------------------------------------------------------
  const loadClientData = async (clientNumber: number) => {
    try {
      const producersData = await fetchProducers(clientNumber);
      setProducers(producersData);

      const { data: dataBox } = await getAllClientBoxes(clientNumber);
      if (dataBox) setBoxes(dataBox);

      const { data: dataBrands } = await getAllClientBrands(clientNumber);
      if (dataBrands) setBrands(dataBrands);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const loadClientDataManual = async (clientNumber: number) => {
    try {
      loadClientData(clientNumber);
      setProducer(null);
      setProducerId(null);
      setFarms([]);
      resetProducer();
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const fetchProducers = async (clientNumber: number) => {
    try {
      const { data: dataProducers } = await getAllClientProducers(clientNumber);
      return dataProducers ?? [];
    } catch (err: any) {
      console.error(err);
      return [];
    }
  };

  const loadFarms = async (producerId: number) => {
    try {
      const { data: dataFarms } = await getAllClientProducerFarms(producerId);
      if (dataFarms) setFarms(dataFarms);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectClient = async (_: any, newValue: string | null) => {
    try {
      setWorkflowClient(newValue ?? "");
      resetWorkflowClient();

      if (!newValue) {
        setWorkflowClientId(null);
        setProducers([]);
        setFarms([]);
        setBoxes([]);
        setBrands([]);
        return;
      }

      const found = clients.find((el) => el.name === newValue);
      if (!found) return;

      setWorkflowClientId(found.id!);
      await loadClientData(found.id!);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const isStandardUser = Boolean(elClientId); // o más claro: elClientId !== null
  const disabledAutocomplete = isStandardUser; // Si es un usuario normal, no debe cambiar Exportador

  return (
    <>
      {showExporters ? (
        <Grid item xs={12} sm={6} md={6}>
          <Autocomplete
            id="clients_list"
            value={clientName || ""}
            options={clients.map((el: ClientI) => el.name) ?? []}
            onChange={handleSelectClient}
            fullWidth
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Exportador" />
            )}
          />
        </Grid>
      ) : null}
        <DividerComponent label="EXPORTADORA">
        <Popconfirm
          title="¿Estás seguro de refrescar los productores?"
          onConfirm={() => {
            loadClientDataManual(elClientId! ?? wfClientId!);
          }}
          okText="Proceder"
          cancelText="Cancelar"
        >
          <Button icon={<FileSyncOutlined />} size="middle" type="dashed">
            Productores
          </Button>
        </Popconfirm>
      </DividerComponent>
      <Producers
        producers={producers}
        onLoadFarms={loadFarms}
        onClearFarms={() => setFarms([])}
        enqueueSnackbar={enqueueSnackbar}
      />
      <Farms farms={farms} />
      <Brands brands={brands} />
      <Boxes boxes={boxes} />
      <Magap />
      <Cajas />
      <CajasEstiba />
      <ConfigProvider
        theme={{
          components: {
            Collapse: {
              contentPadding: "0px",
            },
          },
          token: {
            motionDurationSlow: "0.1s",
            motionDurationMid: "0.1s",
          },
        }}
      >
        <Collapse
          style={{
            width: "100%",
            minWidth: "100%",
            padding: "0px 5px 20px 20px",
            borderTop: "1px dashed darkgreen",
            borderBottom: "1px dashed darkgreen",
            marginTop: "30px",
            borderRadius: "0px",
            backgroundColor: "#eaecee",
          }}
          accordion={true}
          expandIconPosition="end"
          items={[
            {
              key: "1",
              label: (
                <p
                  style={{ color: "navy", fontWeight: "bolder", fontSize: 12 }}
                >
                  OPCIONAL
                </p>
              ),
              children: (
                <Grid container spacing={1} sx={{ width: "100%" }}>
                  <ProducersOptional
                    producers={producers}
                    onLoadFarms={loadFarms}
                    onClearFarms={() => setFarms([])}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                  <FarmsOptional farms={farms} />
                  <BrandsOptional brands={brands} />
                  <BoxesOptional boxes={boxes} />
                  <MagapOptional />
                  <CajasOptional />
                  <CajasEstibaOptional />
                </Grid>
              ),
            },
          ]}
          bordered={false}
          destroyInactivePanel={true}
          defaultActiveKey={["0"]}
        />
      </ConfigProvider>
    </>
  );
};

interface ProducersPropsI {
  producers: ClientProducerI[];
  onLoadFarms: (producerId: number) => Promise<void>;
  onClearFarms: () => void;
  enqueueSnackbar: (message: string, options?: any) => void;
}

export const Producers = ({
  producers,
  onLoadFarms,
  onClearFarms,
  enqueueSnackbar,
}: ProducersPropsI) => {
  const producer = useWorkflowContainerFullStore((state) => state.producer);
  const setProducer = useWorkflowContainerFullStore(
    (state) => state.setProducer
  );
  const setProducerId = useWorkflowContainerFullStore(
    (state) => state.setProducerId
  );
  const resetProducer = useWorkflowContainerFullStore(
    (state) => state.resetProducer
  );

  const handleSelectClientProducer = async (newValue: string | null) => {
    try {
      setProducer(newValue ?? null);
      resetProducer();

      if (!newValue) {
        // Si se limpia el select, limpiamos farms en el padre
        onClearFarms();
        return;
      }

      const found = producers.find((el) => el.name === newValue);
      if (!found) return;

      setProducerId(found.id!);
      await onLoadFarms(found.id!);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Select
        options={producers.map((el) => ({ label: el.name, value: el.name }))}
        value={producer}
        placeholder="Productor"
        // suffixIcon={
        //   producer ? (
        //     <p style={{ fontSize: 6, color: "black", fontWeight: "bold" }}>
        //       Productor
        //     </p>
        //   ) : (
        //     <p style={{ color: "black", fontWeight: "bold" }}>Productor</p>
        //   )
        // }
        allowClear={true}
        onChange={handleSelectClientProducer}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

interface FarmsPropsI {
  farms: ClientProducerFarmI[];
}
const Farms = ({ farms }: FarmsPropsI) => {
  const balerCode = useWorkflowContainerFullStore((state) => state.balerCode);
  const farm = useWorkflowContainerFullStore((state) => state.farm);
  const zone = useWorkflowContainerFullStore((state) => state.zone);
  const setFarm = useWorkflowContainerFullStore((state) => state.setFarm);
  const setFarmId = useWorkflowContainerFullStore((state) => state.setFarmId);
  const setBalerCode = useWorkflowContainerFullStore(
    (state) => state.setBalerCode
  );
  const setZone = useWorkflowContainerFullStore((state) => state.setZone);

  const resetFarm = useWorkflowContainerFullStore((state) => state.resetFarm);

  const handleSelectClientProducerFarm = (newValue: string | null) => {
    setFarm(newValue ?? null);

    if (!newValue) {
      resetFarm();
      return;
    }

    const fill = farms.find((el) => el.name === newValue);
    if (!fill) return;

    setFarmId(fill.id!);
    setZone(fill.zone);
    setBalerCode(fill.balerCode);
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Select
          options={farms.map((el) => ({ label: el.name, value: el.name }))}
          value={farm}
          placeholder="Hacienda"
          // suffixIcon={
          //   farm ? (
          //     <p style={{ fontSize: 8, color: "black", fontWeight: "bold" }}>
          //       Hacienda
          //     </p>
          //   ) : (
          //     <p style={{ color: "black", fontWeight: "bold" }}>Hacienda</p>
          //   )
          // }
          allowClear={true}
          onChange={handleSelectClientProducerFarm}
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={zone}
          readOnly
          suffix={
            zone ? (
              <p style={{ color: "black", fontSize: 8, fontWeight: "bold" }}>
                Zona
              </p>
            ) : (
              <p style={{ color: "black", fontSize: 11, fontWeight: "bold" }}>
                Zona
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={balerCode}
          onChange={(e) => setBalerCode(e.target.value)}
          suffix={
            balerCode ? (
              <p style={{ color: "black", fontSize: 8, fontWeight: "bold" }}>
                Empacadora
              </p>
            ) : (
              <p style={{ color: "black", fontSize: 11, fontWeight: "bold" }}>
                Código de Empacadora
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
    </>
  );
};

const Brands = ({ brands }: { brands: ClientBrandI[] }) => {
  const brand = useWorkflowContainerFullStore((state) => state.brand);
  const setBrand = useWorkflowContainerFullStore((state) => state.setBrand);
  const setBrandId = useWorkflowContainerFullStore((state) => state.setBrandId);

  const handleSelectBrand = (newValue: string | null) => {
    if (!newValue) {
      setBrand(null);
      setBrandId(null);
      return;
    }

    setBrand(newValue);

    const fill = brands.find((el) => el.name === newValue);
    if (!fill) return;

    setBrandId(fill.id!);
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={brands.map((el) => ({ label: el.name, value: el.name }))}
        value={brand}
        placeholder="Marca"
        // suffixIcon={
        //   brand ? (
        //     <p style={{ fontSize: 8, color: "black", fontWeight: "bold" }}>
        //       Marca
        //     </p>
        //   ) : (
        //     <p style={{ color: "black", fontWeight: "bold" }}>Marca</p>
        //   )
        // }
        allowClear={true}
        onChange={handleSelectBrand}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const Boxes = ({ boxes }: { boxes: ClientBoxI[] }) => {
  const box = useWorkflowContainerFullStore((state) => state.box);
  const setBox = useWorkflowContainerFullStore((state) => state.setBox);
  const setBoxId = useWorkflowContainerFullStore((state) => state.setBoxId);

  const handleSelectBox = (newValue: string | null) => {
    if (!newValue) {
      setBox(null);
      setBoxId(null);
      return;
    }

    setBox(newValue);

    const fill = boxes.find((el) => el.name === newValue);
    if (!fill) return;

    setBoxId(fill.id!);
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={boxes.map((el) => ({ label: el.name, value: el.name }))}
        value={box}
        placeholder="Tipo de Caja"
        // suffixIcon={
        //   box ? (
        //     <p style={{ fontSize: 8, color: "black", fontWeight: "bold" }}>
        //       Caja
        //     </p>
        //   ) : (
        //     <p style={{ color: "black", fontWeight: "bold" }}>Tipo de Caja</p>
        //   )
        // }
        allowClear={true}
        onChange={handleSelectBox}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const Magap = () => {
  const magap = useWorkflowContainerFullStore((state) => state.magap);
  const setMagap = useWorkflowContainerFullStore((state) => state.setMagap);

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Input
        style={{ width: "100%" }}
        value={magap || ""}
        onChange={(e) => setMagap(e?.target?.value?.toUpperCase() ?? "")}
        prefix={
          magap ? (
            <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
              Magap
            </p>
          ) : (
            <p style={{ fontWeight: "bold", color: "black" }}>Magap</p>
          )
        }
      />
    </Grid>
  );
};

const Conductor = ({ width }: { width: string }) => {
  const driver = useWorkflowContainerFullStore((state) => state.driver);
  const setDriver = useWorkflowContainerFullStore((state) => state.setDriver);

  const placa = useWorkflowContainerFullStore((state) => state.placa);
  const setPlaca = useWorkflowContainerFullStore((state) => state.setPlaca);

  return (
    <>
      {width === "xs" ? (
        <>
          <Grid item xs={12}>
            <Input
              value={driver || ""}
              onChange={(e) => setDriver(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Conductor
                </p>
              }
              placeholder="Nombre y Apellido"
              style={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={placa || ""}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Placa
                </p>
              }
              placeholder=""
              style={{ width: "100%" }}
            />
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={driver || ""}
              onChange={(e) => setDriver(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Conductor
                </p>
              }
              placeholder="Nombre y Apellido"
              style={{ width: "60%" }}
            />
            <Input
              value={placa || ""}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Placa
                </p>
              }
              placeholder=""
              style={{ width: "40%" }}
            />
          </Space.Compact>
        </Grid>
      )}
    </>
  );
};

const CompañíaDeTransporte = () => {
  const companyTransport = useWorkflowContainerFullStore(
    (state) => state.companyTransport
  );
  const setCompanyTransport = useWorkflowContainerFullStore(
    (state) => state.setCompanyTransport
  );

  return (
    <Grid item xs={12}>
      <Input
        value={companyTransport || ""}
        onChange={(e) => setCompanyTransport(e.target.value)}
        addonBefore={
          <p
            style={{
              fontSize: "14px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            Compañía
          </p>
        }
        placeholder=""
      />
    </Grid>
  );
};

export const ProducersOptional = ({
  producers,
  onLoadFarms,
  onClearFarms,
  enqueueSnackbar,
}: ProducersPropsI) => {
  const producer = useWorkflowContainerFullStore(
    (state) => state.producerOptional
  );
  const setProducer = useWorkflowContainerFullStore(
    (state) => state.setProducerOptional
  );
  const setProducerId = useWorkflowContainerFullStore(
    (state) => state.setProducerOptionalId
  );
  const resetProducer = useWorkflowContainerFullStore(
    (state) => state.resetProducerOptional
  );

  const handleSelectClientProducer = async (newValue: string | null) => {
    try {
      setProducer(newValue ?? "");
      resetProducer();

      if (!newValue) {
        // Si se limpia el select, limpiamos farms en el padre
        onClearFarms();
        return;
      }

      const found = producers.find((el) => el.name === newValue);
      if (!found) return;

      setProducerId(found.id!);
      await onLoadFarms(found.id!);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Select
        options={producers.map((el) => ({ label: el.name, value: el.name }))}
        value={producer}
        placeholder="Productor"
        suffixIcon={
          producer ? (
            <p style={{ fontSize: 6, color: "black", fontWeight: "bold" }}>
              Productor
            </p>
          ) : (
            <p style={{ color: "black", fontWeight: "bold" }}>Productor</p>
          )
        }
        allowClear={true}
        onChange={handleSelectClientProducer}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const FarmsOptional = ({ farms }: FarmsPropsI) => {
  const balerCode = useWorkflowContainerFullStore(
    (state) => state.balerCodeOptional
  );
  const farm = useWorkflowContainerFullStore((state) => state.farmOptional);
  const zone = useWorkflowContainerFullStore((state) => state.farmOptionalZone);
  const setFarm = useWorkflowContainerFullStore(
    (state) => state.setFarmOptional
  );
  const setFarmId = useWorkflowContainerFullStore(
    (state) => state.setFarmOptionalId
  );
  const setBalerCode = useWorkflowContainerFullStore(
    (state) => state.setBalerCodeOptional
  );
  const setZone = useWorkflowContainerFullStore(
    (state) => state.setFarmOptionalZone
  );

  const resetFarm = useWorkflowContainerFullStore(
    (state) => state.resetFarmOptional
  );

  const handleSelectClientProducerFarm = (newValue: string | null) => {
    setFarm(newValue ?? "");

    if (!newValue) {
      resetFarm();
      return;
    }

    const fill = farms.find((el) => el.name === newValue);
    if (!fill) return;

    setFarmId(fill.id!);
    setZone(fill.zone);
    setBalerCode(fill.balerCode);
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Select
          options={farms.map((el) => ({ label: el.name, value: el.name }))}
          value={farm}
          placeholder="Hacienda"
          suffixIcon={
            farm ? (
              <p style={{ fontSize: 8, color: "black", fontWeight: "bold" }}>
                Hacienda
              </p>
            ) : (
              <p style={{ color: "black", fontWeight: "bold" }}>Hacienda</p>
            )
          }
          allowClear={true}
          onChange={handleSelectClientProducerFarm}
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={zone}
          readOnly
          suffix={
            zone ? (
              <p style={{ color: "black", fontSize: 8, fontWeight: "bold" }}>
                Zona
              </p>
            ) : (
              <p style={{ color: "black", fontSize: 11, fontWeight: "bold" }}>
                Zona
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={balerCode}
          onChange={(e) => setBalerCode(e.target.value)}
          suffix={
            balerCode ? (
              <p style={{ color: "black", fontSize: 8, fontWeight: "bold" }}>
                Empacadora
              </p>
            ) : (
              <p style={{ color: "black", fontSize: 11, fontWeight: "bold" }}>
                Código de Empacadora
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
    </>
  );
};

const BrandsOptional = ({ brands }: { brands: ClientBrandI[] }) => {
  const brand = useWorkflowContainerFullStore((state) => state.brandOptional);
  const setBrand = useWorkflowContainerFullStore(
    (state) => state.setBrandOptional
  );
  const setBrandId = useWorkflowContainerFullStore(
    (state) => state.setBrandOptionalId
  );

  const handleSelectBrand = (newValue: string | null) => {
    if (!newValue) {
      setBrand("");
      setBrandId(null);
      return;
    }

    setBrand(newValue);

    const fill = brands.find((el) => el.name === newValue);
    if (!fill) return;

    setBrandId(fill.id!);
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={brands.map((el) => ({ label: el.name, value: el.name }))}
        value={brand}
        placeholder="Marca"
        suffixIcon={
          brand ? (
            <p style={{ fontSize: 8, color: "black", fontWeight: "bold" }}>
              Marca
            </p>
          ) : (
            <p style={{ color: "black", fontWeight: "bold" }}>Marca</p>
          )
        }
        allowClear={true}
        onChange={handleSelectBrand}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const BoxesOptional = ({ boxes }: { boxes: ClientBoxI[] }) => {
  const box = useWorkflowContainerFullStore((state) => state.boxOptional);
  const setBox = useWorkflowContainerFullStore((state) => state.setBoxOptional);
  const setBoxId = useWorkflowContainerFullStore(
    (state) => state.setBoxOptionalId
  );

  const handleSelectBox = (newValue: string | null) => {
    if (!newValue) {
      setBox("");
      setBoxId(null);
      return;
    }

    setBox(newValue);

    const fill = boxes.find((el) => el.name === newValue);
    if (!fill) return;

    setBoxId(fill.id!);
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={boxes.map((el) => ({ label: el.name, value: el.name }))}
        value={box}
        placeholder="Tipo de Caja"
        suffixIcon={
          box ? (
            <p style={{ fontSize: 8, color: "black", fontWeight: "bold" }}>
              Caja
            </p>
          ) : (
            <p style={{ color: "black", fontWeight: "bold" }}>Tipo de Caja</p>
          )
        }
        allowClear={true}
        onChange={handleSelectBox}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const MagapOptional = () => {
  const magap = useWorkflowContainerFullStore((state) => state.magapOptional);
  const setMagap = useWorkflowContainerFullStore(
    (state) => state.setMagapOptional
  );

  return (
    <Grid item xs={12} md={6} lg={4}>
      <InputNumber
        style={{ width: "100%" }}
        value={magap || null}
        onChange={(value) => setMagap(value)}
        addonBefore={
          <p
            style={{
              fontSize: "12px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            Magap
          </p>
        }
      />
    </Grid>
  );
};

const CajasOptional = () => {
  const numCajas = useWorkflowContainerFullStore(
    (state) => state.numCajasOptional
  );
  const setNumCajas = useWorkflowContainerFullStore(
    (state) => state.setNumCajasOptional
  );

  return (
    <Grid item xs={6}>
      <InputNumber
        style={{ width: "100%" }}
        value={numCajas || null}
        onChange={(value) => setNumCajas(value)}
        addonBefore={
          <p
            style={{
              fontSize: "12px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            N° Cajas
          </p>
        }
      />
    </Grid>
  );
};

const CajasEstibaOptional = () => {
  const cajasEstiba = useWorkflowContainerFullStore(
    (state) => state.cajasEstibaOptional
  );
  const setCajasEstiba = useWorkflowContainerFullStore(
    (state) => state.setCajasEstibaOptional
  );

  return (
    <Grid item xs={6}>
      <InputNumber
        style={{ width: "100%" }}
        value={cajasEstiba || null}
        onChange={(value) => setCajasEstiba(value)}
        addonBefore={
          <p
            style={{
              fontSize: "10px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
              textWrap: "wrap",
            }}
          >
            Cajas Doble Estiba
          </p>
        }
      />
    </Grid>
  );
};
